import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import LoadingProgressBar from './LoadingProgressBar';

const COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#84cc16'];

const ChartRenderer = ({ data, type, title, description }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    let progressInterval;
    
    const startLoading = () => {
      setIsLoading(true);
      setLoadingProgress(0);
      
      progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            setTimeout(() => setIsLoading(false), 200);
            return 100;
          }
          return prev + 8;
        });
      }, 120);
    };

    startLoading();

    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [data, type]);

  useEffect(() => {
    setIsLoading(true);
    setLoadingProgress(0);
  }, [data, type]);

  if (!data) {
    return <div className="text-center py-8 text-gray-500">No chart data provided</div>;
  }
  
  if (!data.data && !Array.isArray(data)) {
    return <div className="text-center py-8 text-gray-500">No data available</div>;
  }
  
  const chartData = Array.isArray(data) ? data : data.data;
  
  if (!Array.isArray(chartData)) {
    return <div className="text-center py-8 text-gray-500">Invalid data format</div>;
  }
  
  if (chartData.length === 0) {
    return <div className="text-center py-8 text-gray-500">No data points available</div>;
  }

  const LoadingChart = () => (
    <div className="w-full h-80 flex flex-col items-center justify-center p-8">
      <div className="w-full max-w-sm">
        <LoadingProgressBar 
          progress={loadingProgress} 
          text="Loading chart visualization..." 
        />
      </div>
      <div className="mt-4 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
        <div className="text-slate-600 text-xs">Processing data...</div>
      </div>
    </div>
  );

  const renderChart = () => {
    if (isLoading) {
      return <LoadingChart />;
    }

    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                dataKey={Object.keys(chartData[0] || {})[0] || 'name'} 
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickLine={{ stroke: '#cbd5e1' }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickLine={{ stroke: '#cbd5e1' }}
                axisLine={{ stroke: '#cbd5e1' }}
              />
              <Tooltip 
                formatter={(value, name) => [
                  typeof value === 'number' ? value.toLocaleString() : value,
                  name
                ]}
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              {Object.keys(chartData[0] || {}).slice(1).map((key, index) => (
                <Bar 
                  key={key} 
                  dataKey={key} 
                  fill={COLORS[index % COLORS.length]}
                  name={key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                  radius={[2, 2, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                dataKey={Object.keys(chartData[0] || {})[0] || 'name'} 
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickLine={{ stroke: '#cbd5e1' }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickLine={{ stroke: '#cbd5e1' }}
                axisLine={{ stroke: '#cbd5e1' }}
              />
              <Tooltip 
                formatter={(value, name) => [
                  typeof value === 'number' ? value.toLocaleString() : value,
                  name
                ]}
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              {Object.keys(chartData[0] || {}).slice(1).map((key, index) => (
                <Line 
                  key={key} 
                  type="monotone" 
                  dataKey={key} 
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={3}
                  dot={{ fill: COLORS[index % COLORS.length], strokeWidth: 2, r: 4 }}
                  name={key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
      case 'donut':
        const pieData = chartData.map((item, index) => ({
          name: item[Object.keys(item || {})[0]] || item.name || 'Unknown',
          value: item[Object.keys(item || {})[1]] || item.value || 0,
          color: COLORS[index % COLORS.length]
        }));

        const pieTotal = pieData.reduce((sum, item) => sum + (item.value || 0), 0);

        return (
          <div className="flex w-full" style={{ height: 320 }}>
            {/* Left side: Labels with percentages */}
            <div className="w-40 flex flex-col justify-center pr-3 space-y-2 overflow-y-auto">
              {pieData.map((item, index) => {
                const percent = pieTotal > 0 ? ((item.value / pieTotal) * 100).toFixed(1) : 0;
                return (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-slate-600 truncate flex-1" title={item.name}>
                      {item.name}
                    </span>
                    <span className="font-semibold text-slate-800 whitespace-nowrap">
                      {percent}%
                    </span>
                  </div>
                );
              })}
            </div>
            
            {/* Right side: Donut chart */}
            <div className="flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={type === 'donut' ? 100 : 100}
                    innerRadius={type === 'donut' ? 50 : 0}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [value.toLocaleString(), 'Value']}
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case 'none':
        return <div className="text-center py-8 text-gray-500">Chart not available for this analysis</div>;

      default:
        return (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                dataKey={Object.keys(chartData[0] || {})[0] || 'name'} 
                tick={{ fontSize: 12, fill: '#64748b' }}
              />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
              <Tooltip 
                formatter={(value, name) => [
                  typeof value === 'number' ? value.toLocaleString() : value,
                  name
                ]}
              />
              <Legend />
              {Object.keys(chartData[0] || {}).slice(1).map((key, index) => (
                <Bar 
                  key={key} 
                  dataKey={key} 
                  fill={COLORS[index % COLORS.length]}
                  name={key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                  radius={[2, 2, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        {title && (
          <h4 className="font-semibold text-slate-800 text-base mb-1">{title}</h4>
        )}
        {description && (
          <p className="text-sm text-slate-600">{description}</p>
        )}
      </div>
      
      {renderChart()}
    </div>
  );
};

export default ChartRenderer;
